import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';


export const usePressedKeys = () => {
    const [pressedKeys, setPressedKeys] = useState(new Set());

    useEffect(() => {
        const handleKeyDown = event => {
            setPressedKeys(prevKeys => new Set(prevKeys).add(event.key));
        };

        const handleKeyUp = event => {
            setPressedKeys(prevKeys => {
                const updatedKeys = new Set(prevKeys);
                updatedKeys.delete(event.key);
                return updatedKeys;
            });
        };

        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
        };
    }, []);

    return pressedKeys;
};

export const useKeyPress = targetKey => {
    const [keyPressed, setKeyPressed] = React.useState(false);
  
    const downHandler = ({ key }) => {
      if (key === targetKey) setKeyPressed(true);
    };
  
    const upHandler = ({ key }) => {
      if (key === targetKey) setKeyPressed(false);
    };
  
    React.useEffect(() => {
      window.addEventListener('keydown', downHandler);
      window.addEventListener('keyup', upHandler);
  
      return () => {
        window.removeEventListener('keydown', downHandler);
        window.removeEventListener('keyup', upHandler);
      };
    }, []);
  
    return keyPressed;
  };

export const useHistory = initialState => {
    const [index, setIndex] = useState(0);
    const [history, setHistory] = useState([initialState]);

    const setState = (action, overwrite = false) => {
        const newState = typeof action === "function" ? action(history[index]) : action;
        if (overwrite) {
            const historyCopy = [...history];
            historyCopy[index] = newState;
            setHistory(historyCopy);
        } else {
            const updatedState = [...history].slice(0, index + 1);
            setHistory([...updatedState, newState]);
            setIndex(prevState => prevState + 1);
        }
    };

    const undo = () => index > 0 && setIndex(prevState => prevState - 1);
    const redo = () => index < history.length - 1 && setIndex(prevState => prevState + 1);

    return [history[index], setState, undo, redo];
};



export const useZoomControl = (zoom, zoomCenter) => {
    useEffect(() => {
        const handleCtrlScroll = (event) => {
            if (event.ctrlKey) {
                event.preventDefault();
                event.stopPropagation();
            }
        };

        window.focus();

        window.addEventListener("wheel", handleCtrlScroll, { passive: false });
        return () => {
            window.removeEventListener("wheel", handleCtrlScroll);
        };
    }, [zoom, zoomCenter]);
};



export const useCanvasDimensions = (canvasParentRef, canvasRef, onDimensionChange) => {
    useLayoutEffect(() => {
        const updateDimensions = () => {
            const canvasContainer = canvasParentRef.current;
            const canvas = canvasRef.current;
            if (canvas && canvasContainer) {
                canvas.style.height = "0px";
                onDimensionChange({  
                        width: canvasContainer.clientWidth,
                        height: canvasContainer.clientHeight,
                    },
                );
                canvas.style.height = "unset";
            }
        };

        updateDimensions();

        const resizeListener = () => {
            updateDimensions();
        };

        window.addEventListener("resize", resizeListener);

        return () => {
            window.removeEventListener("resize", resizeListener);
        };
    }, [canvasRef]);
};


export const useWindowReloadWarning = (hasElementChanges) => {
    useEffect(() => {
        const beforeUnloadHandler = (e) => {
            if (!hasElementChanges) return;
            var confirmationMessage =
                "It looks like you have been editing something. If you leave before saving, your changes will be lost.";
            (e || window.event).returnValue = confirmationMessage; //Gecko + IE
            return confirmationMessage; //Gecko + Webkit, Safari, Chrome etc.
        };
        window.addEventListener("beforeunload", beforeUnloadHandler);
        return () => {
            window.removeEventListener("beforeunload", beforeUnloadHandler);
        };
    }, [hasElementChanges]);
}